<template>
    <div class="c-blog__filters__categories">
        <p class="c-blog__filters__label">{{ $t('blog.category.label') }}</p>
        <div :id="'accordion-'+domId" class="c-accordions__item" :class="{'is-opened' : state.open}" ref="collapse">
            <p class="c-accordions__title">
                <button 
                    type="button"
                    :id="domId"
                    class="c-accordions__title__toggler o-wysiwyg"
                    ref="toggler"
                    :aria-label="$t('blog.category.placeholder')"
                    aria-expanded="false"
                    :aria-controls="'panel-'+domId"
                    @click="clickHandler"
                >
                    <span class="c-accordions__title__toggler__label">{{ $t('blog.category.placeholder') }}</span>
                    <Chevron />
                </button>
            </p>
    
            <div 
                :id="'panel-'+domId"
                class="c-accordions__container"
                ref="container"
                role="region"
                :aria-labelledby="domId"
            >
            <div class="c-accordions__wrapper">
                <div class="c-accordions__content o-wysiwyg" ref="content">
                    <template v-for="(category, id) in categories">
                        <div class="c-blog__filters__categories__field c-blog__filters__categories__field--first">
                            <span class="c-blog__filters__categories__heading">{{ category.title }}</span>

                            <div class="c-blog__filters__categories__field__controls">
                                <button @click="selectAllClickHandler(id, true)" type="button" :aria-label="$t('interactiveMap.categories.select_aria') + ' ' + category.title" :tabindex="(state.isOpen)?'':'-1'">{{ $t('interactiveMap.categories.select') }}</button>
                                <button @click="selectAllClickHandler(id, false)" type="button" :aria-label="$t('interactiveMap.categories.deselect_aria') + ' ' + category.title" :tabindex="(state.isOpen)?'':'-1'">{{ $t('interactiveMap.categories.deselect') }}</button>
                            </div>
                        </div>

                        <template v-if="Object.keys(category.children).length">
                            <template v-for="(childrenCategory, childrenSlug) in category.children">
                                <CheckboxField 
                                    modifiers='c-blog__filters__categories__field--second' 
                                    :id="childrenSlug" 
                                    :title="childrenCategory.title" 
                                    :state="state.open" 
                                    :checked="checkState(childrenSlug)" 
                                    @update:checked="inputChangeHandler(childrenSlug, $event)"
                                />
                                <template v-if="Object.keys(childrenCategory.children).length">
                                    <template v-for="(thirdChildrenCategory, thirdChildrenSlug) in childrenCategory.children">
                                        <CheckboxField 
                                            modifiers='c-blog__filters__categories__field--third' 
                                            :id="thirdChildrenSlug" 
                                            :title="thirdChildrenCategory.title" 
                                            :state="state.open" 
                                            :checked="checkState(thirdChildrenSlug)" 
                                            @update:checked="inputChangeHandler(thirdChildrenSlug, $event)"
                                        />
                                    </template>
                                </template>
                            </template>
                        </template>
                    </template>
                </div>
            </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { toRaw, nextTick } from 'vue';

    import CheckboxField from './CheckboxField.vue';
    import Checkmark from './icons/Checkmark.vue';
    import Chevron from './icons/Chevron.vue';

    export default {
        name: 'Categories',
        components: {
            Chevron,
            Checkmark,
            CheckboxField   
        },
        props: {
            categories: {
                type: Object,
                default: {},
            },
            activeFilters: {
                type: Object,
                default: null,
            },
        },
        data() {
            return {
                lang: window.Store.app.lang,
                domId: 'categories',
                state: {
                    open: false,
                    openClass: 'is-opened'
                },
                activeCategories: [],
            }
        },
        watch: {},
        mounted() {
            document.addEventListener('click', this.bodyClickHandler);
            document.addEventListener('keyup', this.keyUpHandler);
        },
        computed: { },
        methods: {
            /**
            **  Returns if the slug is in the activeFilters array(checkbox is checked)
            */
            checkState(slug){
                if(this.activeFilters?.categories?.indexOf(slug) > -1){
                    return true;
                }
                else {
                    return false;
                }
            },
            /**
            **  Handle the checkbox's change event
            */
            inputChangeHandler(slug, isChecked){
                if(isChecked){
                    this.activeFilters.categories.push(slug);
                }
                else{
                    this.activeFilters.categories.splice(this.activeFilters.categories.indexOf(slug), 1);
                }
                
                this.$emit("update:states");
            },
            /**
            **  Handle the select-all and deselect-all buttons
            */
            async selectAllClickHandler(slug, isChecked){
                if(isChecked){
                    // Select-All
                    if(Object.keys(this.categories[slug].children).length){
                        const childrenCategories = toRaw(this.categories[slug].children);
                        for (const childrenSlug in childrenCategories) {
                            this.activeFilters.categories.push(childrenSlug);

                            if(Object.keys(childrenCategories[childrenSlug].children).length){
                                const secondChildrenCategories = toRaw(childrenCategories[childrenSlug].children);
                                for (const secondChildrenSlug in secondChildrenCategories) {
                                    this.activeFilters.categories.push(secondChildrenSlug);
                                }
                            }
                        }
                    }
                }
                else{
                    // Deselect-All
                    if(Object.keys(this.categories[slug].children).length){
                        const childrenCategories = toRaw(this.categories[slug].children);
                        for (const childrenSlug in childrenCategories) {
                            this.activeFilters.categories.splice(this.activeFilters.categories.indexOf(childrenSlug), 1);

                            if(Object.keys(childrenCategories[childrenSlug].children).length){
                                const secondChildrenCategories = toRaw(childrenCategories[childrenSlug].children);
                                for (const secondChildrenSlug in secondChildrenCategories) {
                                    this.activeFilters.categories.splice(this.activeFilters.categories.indexOf(secondChildrenSlug), 1);
                                }
                            }
                        }
                    }
                }

                await nextTick();
                this.$emit("update:states");
            },
            /**
            **  Accordion ClickHandler method and Open animation
            */
            clickHandler(){
                if(!this.state.open){
                    window.Store.scrollbar.gsap.obj.to(this.$refs.container, {
                        duration: 0,
                        height: this.$refs.content.offsetHeight,
                        opacity: 1,
                        ease: 'expo.inOut',
                        onComplete: () => {
                            window.Store.scrollbar.gsap.obj.set(this.$refs.container, {
                                height: 'auto'
                            });

                            /* Update Scrollbar */
                            window.Store.scrollbar.update();
                        }
                    });
                    this.$refs.toggler.setAttribute('aria-expanded', 'true');
                    this.state.open = true;

                    /* Activate tabindex of interactive element */
                    this.toggleLinks(this.$refs.container, '');
                }
                else{
                    this.close();
                }
            },
            /**
            **  Accordion Close animation
            */
            close(){
                window.Store.scrollbar.gsap.obj.to(this.$refs.container, {
                    duration: 0,
                    height: 0,
                    opacity: 0,
                    ease: 'expo.inOut',
                    clearProps: 'all',
                    onComplete: () => {
                        /* Update Scrollbar */
                        window.Store.scrollbar.update();
                    }
                });
                this.$refs.collapse.classList.remove(this.state.openClass);
                this.$refs.toggler.setAttribute('aria-expanded', 'false');
                this.state.open = false;

                /* Deactivate tabindex of interactive element */
                this.toggleLinks(this.$refs.container, -1);
            },
            /**
             * Activate/Deactivate tabindex on interactive elements of accordion's content
             */
            toggleLinks(containerElement, value) {
                const clickableElements = containerElement.querySelectorAll('a, button, input');

                for(const element of clickableElements){
                    element.setAttribute('tabindex', value);
                }
            },
            /**
            **  Handle the "blur" event for accordions
            */
            bodyClickHandler(e){
                if(!this.state.open){
                    return;
                }
                
                if(!this.$el.contains(e.target)){
                    this.close();
                }
            },
            /**
            **  Handle the "Escape" key event usage
            */
            keyUpHandler(e){
                if(!this.state.open){
                    return;
                }
                else if(e.key === 'Escape'){
                    this.close();
                }
            },
        }
    }
</script>